import { useEffect, useMemo } from 'react';

import { FokParameterChoice } from '@fora-monorepo/employer-my-pages-common/types';
import { isPensionParameter } from '@fora-monorepo/employer-my-pages-common/utils';
import { useDatePickerInput } from '@foradevelopment/fora-ui';

import { MarkupAndParametersFormProps } from './types';
import {
  getParameterEndDateMin,
  getParameterStartDateMin,
  getParameterTKeys,
  validateParameterEndDate,
} from '../../../common/utils';
import { useAppSelector } from '../../../redux/hooks';
import { ParameterDateInputFields } from './ParameterDateInputFields';
import { useTypedTranslation } from '../../../i18n/i18n';

export const ActiveParameter = ({
  setParameterChoicesInput,
  employmentStartDate,
  activeParameterChoice,
  fokParameterValidity,
  disabled,
}: {
  activeParameterChoice: FokParameterChoice;
  setParameterChoicesInput: MarkupAndParametersFormProps['setParameterChoicesInput'];
  employmentStartDate: string;
  fokParameterValidity?: { validTo: string | null; validFrom: string };
  disabled: boolean;
}) => {
  const { t } = useTypedTranslation();
  const isPensionParam = isPensionParameter(activeParameterChoice.parameterValue);
  const monthlyReportingReleaseDate = useAppSelector(state => state.foraGlobal.settings.monthlyReportingReleaseDate);

  const startDateInputSettings = useMemo(() => {
    return {
      initValue: activeParameterChoice.startDate,
      minDate: getParameterStartDateMin(monthlyReportingReleaseDate, employmentStartDate, fokParameterValidity),
      maxDate: fokParameterValidity?.validTo,
    };
  }, [activeParameterChoice.startDate, employmentStartDate, fokParameterValidity, monthlyReportingReleaseDate]);

  const startDateInput = useDatePickerInput(startDateInputSettings);

  const endDateInputSettings = useMemo(() => {
    return {
      initValue: activeParameterChoice.endDate ?? '',
      minDate: getParameterEndDateMin(fokParameterValidity, startDateInput.value),
      maxDate: fokParameterValidity?.validTo,
      validate: validateParameterEndDate(fokParameterValidity, startDateInput.value),
    };
  }, [activeParameterChoice.endDate, startDateInput.value, fokParameterValidity]);
  const endDateInput = useDatePickerInput(endDateInputSettings);

  useEffect(
    () => {
      setParameterChoicesInput(prevVal => [
        ...prevVal.filter(selected => selected.fokParameter.parameterValue !== activeParameterChoice.parameterValue),
        {
          fokParameter: {
            parameterValue: activeParameterChoice.parameterValue,
            startDate: startDateInput.value,
            endDate: endDateInput.value || undefined,
          },
          validateInput: () =>
            isPensionParam
              ? endDateInput.validateDateInputValue().length === 0
              : startDateInput.validateDateInputValue().length === 0 &&
                endDateInput.validateDateInputValue().length === 0,
        },
      ]);
    },
    // Only update when inputs have new value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startDateInput.value, endDateInput.value]
  );

  return (
    <ParameterDateInputFields
      startDateInputProps={isPensionParam ? undefined : startDateInput.props}
      endDateInputProps={endDateInput.props}
      activeParameterName={t(getParameterTKeys(activeParameterChoice.parameterValue).name)}
      disabled={disabled}
    />
  );
};
